import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setSelectedStudent } from "../../redux/admin/student.slice";
import { fetchStudents, addStudent, updateStudent } from "../../redux/admin/student.thunk";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const Students = () => {
  const dispatch = useDispatch();
  const limit = 10;

  // Assuming totalStudents comes from your backend meta data
  const {
    students = [],
    totalPages = 1,
    currentPage = 1,
    total = 0, // Your API uses 'total', not 'totalStudents'
    loading = false,
    selectedStudent = null,
  } = useSelector((state) => state.admin?.students || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const initialFormState = {
    admission_number: "", name: "", last_name: "", dob: "", gender: "M", 
    aadhaar_no: "", father_name: "", mother_name: "", guardian_name: "", 
    mother_tongue: "", father_occupation: "", mother_occupation: "", 
    mobile_no: "", alternative_no: "", email: "", address: "", 
    address2: "", state: "", district: "", city: "", pincode: "", 
    school: "", grade: "", status: "active"
  };

  const [formData, setFormData] = useState(initialFormState);

  const states = ["Tamil Nadu", "Kerala", "Karnataka", "Maharashtra"];
  const districtsMap = {
    "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Salem"],
    "Kerala": ["Kochi", "Thiruvananthapuram", "Kozhikode"],
    "Karnataka": ["Bengaluru", "Mysuru", "Mangaluru"],
    "Maharashtra": ["Mumbai", "Pune", "Nagpur"]
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchStudents({ page: currentPage, limit, search: debouncedSearch }));
  }, [dispatch, currentPage, debouncedSearch]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentPage(newPage));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? (checked ? 'active' : 'inactive') : value,
      ...(name === "state" ? { district: "", city: "" } : {}),
    }));
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setActiveTab("basic");
    setShowModal(true);
  };

  const openEditModal = (student) => {
    setIsEditMode(true);
    dispatch(setSelectedStudent(student));
    setFormData({ ...initialFormState, ...student, status: student.status || "active" });
    setActiveTab("basic");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile_no || !formData.admission_number) {
      toastr.warning("ID, Name, and Mobile are required");
      return;
    }
    try {
      if (isEditMode) {
        await dispatch(updateStudent({ id: selectedStudent.id, studentData: formData })).unwrap();
        toastr.success("Profile Synchronized");
      } else {
        await dispatch(addStudent(formData)).unwrap();
        toastr.success("Admission Successful");
      }
      setShowModal(false);
      dispatch(fetchStudents({ page: currentPage, limit, search: debouncedSearch }));
    } catch (err) { toastr.error(err || "Process Failed"); }
  };

  // Generate pagination page numbers
  const renderPagination = () => {
    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button 
          key={i} 
          onClick={() => handlePageChange(i)}
          className={`mnc-page-num ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="mnc-page-bg">
      <div className="container-fluid px-4 py-4">
        {/* HEADER SECTION */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h3 className="fw-800 text-dark mb-1">Student Registry</h3>
            <div className="d-flex align-items-center gap-2">
              <span className="badge-total shadow-sm">Total Students: {total}</span>
              <span className="text-muted small fw-600">| Registry v4.2</span>
            </div>
          </div>
          <button className="btn btn-mnc-primary shadow-sm" onClick={openAddModal}>
            <i className="fas fa-plus-circle me-2"></i>New Admission
          </button>
        </div>

        {/* SEARCH BAR */}
        <div className="search-bar-mnc shadow-sm mb-4">
          <i className="fas fa-search me-3 text-muted"></i>
          <input 
            type="text" 
            placeholder="Search by name, ID, or mobile number..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>

        {/* DATA TABLE */}
        <div className="mnc-card shadow-sm">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead className="mnc-thead">
                <tr>
                  <th className="ps-4">STUDENT IDENTIFICATION</th>
                  <th>ADMISSION ID</th>
                  <th>GRADE</th>
                  <th>STATUS</th>
                  <th className="text-end pe-4">MANAGEMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary spinner-border-sm"></div></td></tr>
                ) : students.map((st) => (
                  <tr key={st.id} className="mnc-row">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div className={`mnc-avatar-mini ${st.status === 'active' ? 'active' : 'inactive'}`}>
                          {st.name?.[0]}
                        </div>
                        <div className="ms-3">
                          <div className="fw-700 text-dark small">{st.full_name || `${st.name} ${st.last_name}`}</div>
                          <div className="text-muted x-small">{st.email || st.mobile_no}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className="mnc-id-tag">{st.admission_number}</span></td>
                    <td><span className="fw-700 text-secondary small">{st.grade || 'N/A'}</span></td>
                    <td>
                      <span className={`mnc-status-pill ${st.status === 'active' ? 'active' : 'inactive'}`}>
                        {st.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-end pe-4">
                      <button className="btn-mnc-outline" onClick={() => openEditModal(st)}>Edit Profile</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* FULL NUMBERED PAGINATION */}
          <div className="mnc-footer-pagination">
            <div className="text-muted x-small fw-700 uppercase">Showing {students.length} of {total}</div>
            <div className="mnc-pagination-hub">
              <button className="mnc-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button className="mnc-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL SYSTEM */}
      {showModal && (
        <div className="mnc-modal-overlay">
          <div className="mnc-modal-window">
            
            {/* MODAL SIDEBAR */}
            <div className="mnc-modal-side">
              <div className="p-4 border-bottom">
                <h6 className="fw-800 text-dark mb-0">Enrollment</h6>
                <p className="x-small text-muted mb-0">Admission Wizard</p>
              </div>
              
              <div className="flex-grow-1 p-2">
                <button className={`mnc-tab-link ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => setActiveTab('basic')}>
                  <i className="fas fa-id-card"></i> Basic Profile
                </button>
                <button className={`mnc-tab-link ${activeTab === 'family' ? 'active' : ''}`} onClick={() => setActiveTab('family')}>
                  <i className="fas fa-users"></i> Family Info
                </button>
                <button className={`mnc-tab-link ${activeTab === 'address' ? 'active' : ''}`} onClick={() => setActiveTab('address')}>
                  <i className="fas fa-location-arrow"></i> Communication
                </button>
              </div>

         {/* REFINED LINEAR STATUS BAR */}
<div className="p-3 mt-auto">
  <div className={`mnc-status-linear ${formData.status === 'active' ? 'is-active' : 'is-inactive'}`}>
    <div className="d-flex align-items-center flex-grow-1">
      <div className="mnc-status-indicator"></div>
      <span className="mnc-status-text">{formData.status?.toUpperCase()}</span>
    </div>
    <div className="form-check form-switch m-0 p-0">
      <input 
        className="form-check-input mnc-switch" 
        type="checkbox" 
        name="status" 
        checked={formData.status === 'active'} 
        onChange={handleChange} 
      />
    </div>
  </div>
</div>


            </div>

            {/* MODAL MAIN CONTENT */}
            <div className="mnc-modal-main">
              <div className="mnc-modal-header">
                <div>
                  <h5 className="fw-800 mb-0">{isEditMode ? 'Update Student Record' : 'Add New Student'}</h5>
                  <span className="text-muted x-small fw-600">ID: {formData.admission_number || 'PENDING'}</span>
                </div>
                <button className="btn-close shadow-none" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="mnc-modal-scroller">
                <form className="row g-3">
                  {activeTab === 'basic' && (
                    <>
                      <div className="col-md-6">
                        <label className="mnc-label">Admission Number *</label>
                        <input name="admission_number" disabled={isEditMode} className="mnc-field" value={formData.admission_number} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Grade</label>
                        <input name="grade" className="mnc-field" value={formData.grade} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">First Name *</label>
                        <input name="name" className="mnc-field" value={formData.name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Last Name</label>
                        <input name="last_name" className="mnc-field" value={formData.last_name} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">DOB</label>
                        <input type="date" name="dob" className="mnc-field" value={formData.dob} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">Gender</label>
                        <select name="gender" className="mnc-select" value={formData.gender} onChange={handleChange}>
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                          <option value="O">Other</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">Mother Tongue</label>
                        <input name="mother_tongue" className="mnc-field" value={formData.mother_tongue} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="mnc-label">Aadhaar Number</label>
                        <input name="aadhaar_no" className="mnc-field" value={formData.aadhaar_no} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {activeTab === 'family' && (
                    <>
                      <div className="col-md-6"><label className="mnc-label">Father's Name</label><input name="father_name" className="mnc-field" value={formData.father_name} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Father's Occupation</label><input name="father_occupation" className="mnc-field" value={formData.father_occupation} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Mother's Name</label><input name="mother_name" className="mnc-field" value={formData.mother_name} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Mother's Occupation</label><input name="mother_occupation" className="mnc-field" value={formData.mother_occupation} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">Guardian Name</label><input name="guardian_name" className="mnc-field" value={formData.guardian_name} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">School Last Attended</label><input name="school" className="mnc-field" value={formData.school} onChange={handleChange} /></div>
                    </>
                  )}

                  {activeTab === 'address' && (
                    <>
                      <div className="col-md-6"><label className="mnc-label">Mobile Number *</label><input name="mobile_no" className="mnc-field" value={formData.mobile_no} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Alternative No</label><input name="alternative_no" className="mnc-field" value={formData.alternative_no} onChange={handleChange} /></div>
                      <div className="col-md-12"><label className="mnc-label">Email Address</label><input name="email" className="mnc-field" value={formData.email} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Address 1</label><input name="address" className="mnc-field" value={formData.address} onChange={handleChange} /></div>
                      <div className="col-md-6"><label className="mnc-label">Address 2</label><input name="address2" className="mnc-field" value={formData.address2} onChange={handleChange} /></div>
                      <div className="col-md-4">
                        <label className="mnc-label">State</label>
                        <select name="state" className="mnc-select" value={formData.state} onChange={handleChange}>
                          <option value="">Select State</option>
                          {states.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="mnc-label">District</label>
                        <select name="district" className="mnc-select" value={formData.district} onChange={handleChange} disabled={!formData.state}>
                          <option value="">Select District</option>
                          {(districtsMap[formData.state] || []).map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4"><label className="mnc-label">Pincode</label><input name="pincode" className="mnc-field" value={formData.pincode} onChange={handleChange} /></div>
                    </>
                  )}
                </form>
              </div>

              <div className="mnc-modal-footer">
                <button className="btn-mnc-discard" onClick={() => setShowModal(false)}>Discard</button>
                <button className="btn-mnc-sync" onClick={handleSubmit}>
                  {isEditMode ? 'Sync Record' : 'Confirm Admission'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .mnc-page-bg { background: #f4f7fa; min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; }
        .fw-800 { font-weight: 800; }
        .x-small { font-size: 0.72rem; }
        .uppercase { text-transform: uppercase; letter-spacing: 0.05em; }

        /* BUTTONS */
        .btn-mnc-primary { background: #0f172a; color: white; border-radius: 10px; padding: 10px 22px; font-weight: 700; border: none; font-size: 0.85rem; }
        .btn-mnc-sync { background: #6366f1; color: white; border-radius: 8px; padding: 8px 24px; font-weight: 700; border: none; font-size: 0.85rem; }
        .btn-mnc-discard { background: #fff; color: #64748b; border-radius: 8px; padding: 8px 24px; font-weight: 700; border: 1px solid #e2e8f0; font-size: 0.85rem; }
        .btn-mnc-outline { background: transparent; border: 1px solid #e2e8f0; padding: 5px 15px; border-radius: 8px; font-weight: 700; font-size: 0.75rem; color: #6366f1; transition: 0.2s; }
        .btn-mnc-outline:hover { background: #6366f1; color: white; }

        /* HEADER BADGE */
        .badge-total { background: #6366f1; color: white; padding: 5px 15px; border-radius: 100px; font-weight: 800; font-size: 0.75rem; }

        /* TABLE */
        .mnc-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; }
        .mnc-thead th { background: #f8fafc; color: #64748b; font-size: 0.65rem; font-weight: 800; padding: 15px; text-transform: uppercase; letter-spacing: 0.1em; }
        .mnc-row { border-bottom: 1px solid #f1f5f9; transition: 0.2s; }
        .mnc-row:hover { background: #fcfdfe; }
        .mnc-avatar-mini { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: white; }
        .mnc-avatar-mini.active { background: #10b981; }
        .mnc-avatar-mini.inactive { background: #f43f5e; }
        .mnc-status-pill { padding: 4px 12px; border-radius: 100px; font-size: 0.6rem; font-weight: 800; }
        .mnc-status-pill.active { background: #dcfce7; color: #15803d; }
        .mnc-status-pill.inactive { background: #fee2e2; color: #b91c1c; }
        .mnc-id-tag { background: #f1f5f9; padding: 4px 8px; border-radius: 6px; font-weight: 700; font-size: 0.75rem; color: #475569; font-family: 'Courier New', Courier, monospace; }

        /* PAGINATION */
        .mnc-footer-pagination { padding: 15px 25px; display: flex; justify-content: space-between; align-items: center; background: #fff; border-top: 1px solid #f1f5f9; }
        .mnc-pagination-hub { display: flex; gap: 5px; align-items: center; }
        .mnc-page-num { width: 32px; height: 32px; border: 1px solid #e2e8f0; background: white; border-radius: 6px; font-size: 0.75rem; font-weight: 700; color: #64748b; }
        .mnc-page-num.active { background: #6366f1; border-color: #6366f1; color: white; }
        .mnc-page-arrow { background: white; border: 1px solid #e2e8f0; width: 32px; height: 32px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #64748b; }
        .mnc-page-arrow:disabled { opacity: 0.4; }

        /* MODAL */
        .mnc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .mnc-modal-window { width: 980px; height: 650px; background: white; border-radius: 24px; display: flex; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .mnc-modal-side { width: 260px; background: #f8fafc; border-right: 1px solid #e2e8f0; display: flex; flex-direction: column; }
        .mnc-modal-main { flex: 1; display: flex; flex-direction: column; }
        .mnc-modal-header { padding: 20px 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .mnc-modal-scroller { flex: 1; overflow-y: auto; padding: 30px; }
        .mnc-modal-footer { padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }

        /* SINGLE LINE STATUS BAR */
        .mnc-status-line { display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; border-radius: 12px; transition: 0.3s; }
        .mnc-status-line.active { background: #ecfdf5; border: 1px solid #10b98140; color: #065f46; }
        .mnc-status-line.inactive { background: #fef2f2; border: 1px solid #ef444440; color: #991b1b; }
        .mnc-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }


        /* LINEAR STATUS DESIGN */
.mnc-status-linear {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-radius: 100px; /* Makes it a pill shape instead of a square */
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

/* Active State: Soft Mint */
.mnc-status-linear.is-active {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

/* Inactive State: Soft Rose */
.mnc-status-linear.is-inactive {
  background: #fff1f2;
  border-color: #fecdd3;
  color: #9f1239;
}

.mnc-status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor; /* Matches the text color automatically */
  margin-right: 10px;
  box-shadow: 0 0 8px currentColor;
}

.mnc-status-text {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.05em;
}

/* CUSTOM SWITCH STYLING */
.mnc-switch {
  cursor: pointer;
  width: 32px !important;
  height: 18px !important;
  margin-top: 0 !important;
  border-color: rgba(0,0,0,0.1) !important;
}

.mnc-switch:checked {
  background-color: #10b981 !important; /* Professional Green */
  border-color: #10b981 !important;
}

.mnc-switch:focus {
  box-shadow: none !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba%280, 0, 0, 0.25%29'/%3e%3c/svg%3e") !important;
}
  
        /* FORM */
        .mnc-tab-link { border: none; background: none; text-align: left; padding: 12px 20px; width: 100%; border-radius: 10px; font-weight: 700; color: #64748b; font-size: 0.82rem; display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .mnc-tab-link.active { background: white; color: #6366f1; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
        .mnc-label { font-size: 0.65rem; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 6px; display: block; }
        .mnc-field, .mnc-select { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; font-weight: 600; color: #1e293b; outline: none; }
        .mnc-field:focus { border-color: #6366f1; }
        .search-bar-mnc { background: white; padding: 12px 20px; border-radius: 14px; display: flex; align-items: center; max-width: 550px; border: 1px solid #e2e8f0; }
        .search-bar-mnc input { border: none; outline: none; width: 100%; font-weight: 600; font-size: 0.85rem; }
      `}</style>
    </div>
  );
};

export default Students;